export type PortalEnvironment = 'sandbox' | 'production';
export type MerchantUserRole = 'owner' | 'developer' | 'finance';
export type MerchantStatus = 'pending' | 'active' | 'suspended' | 'blacklisted';

export type KybDocumentType = 'trade_license' | 'passport' | 'incorporation_doc';

export interface KybDocumentStatus {
  uploaded: boolean;
  incomplete?: boolean;
  file_name?: string | null;
  uploaded_at?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
}

export type KybDocumentsMap = Record<KybDocumentType, KybDocumentStatus>;

export const EMPTY_KYB_DOCUMENTS: KybDocumentsMap = {
  trade_license: { uploaded: false },
  passport: { uploaded: false },
  incorporation_doc: { uploaded: false },
};

export interface PortalUser {
  id: string;
  merchantId: string;
  email: string;
  role: MerchantUserRole;
  totp_enabled?: boolean;
}

export interface WalletChangelogEntry {
  id: number;
  previous_wallet: string;
  new_wallet: string;
  changed_by_type: string;
  changed_by_email: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface SettlementPreview {
  solana_wallet: string;
  configured: boolean;
  merchant_vault: string | null;
  updated_at: string | null;
  updated_by: string | null;
  status: string;
}

export interface MerchantProfile {
  id: string;
  name: string;
  status: MerchantStatus;
  settlement_wallet_configured?: boolean;
  solana_wallet?: string;
  bank_iban: string | null;
  fee_percent: number;
  rolling_reserve_percent: number;
  daily_limit_aed: number;
  webhook_url: string | null;
  trade_license_number: string | null;
  license_expiry_date: string | null;
  corporate_structure: string | null;
  legal_address: string | null;
  registration_country: string | null;
  ubo_full_name: string | null;
  ubo_email: string | null;
  ubo_phone: string | null;
  ubo_nationality: string | null;
  kyb_step: number;
}

export interface Balances {
  available_balance: string;
  pending_balance: string;
  rolling_reserve: string;
  currency: string;
  reserve_percent: number;
}

export interface MeResponse {
  ok: boolean;
  user: PortalUser;
  merchant: MerchantProfile;
  settlement?: SettlementPreview;
  kyb_documents?: KybDocumentsMap;
  balances: Balances;
  financial_operations_locked: boolean;
  error?: string;
}

export interface LoginResponse {
  ok: boolean;
  token?: string;
  requires_2fa?: boolean;
  pending_token?: string;
  error?: string;
}

export interface RegisterResponse extends LoginResponse {
  merchant_id?: string;
  status?: string;
  /** True when account was created on a prior request and we issued a session. */
  resumed?: boolean;
  code?: string;
}

export interface DashboardResponse {
  ok: boolean;
  environment: PortalEnvironment;
  range: string;
  balances: Balances;
  volume_series: { bucket: string; volume: string; tx_count: string }[];
  methods: { method: string; total: string }[];
  error?: string;
}

export interface PayoutRequest {
  id: string;
  amount_display: string;
  currency: string;
  status: string;
  created_at: string;
  executed_at: string | null;
  payout_method: string | null;
  beneficiary_name: string | null;
  bank_iban: string | null;
  crypto_network: string | null;
  crypto_address: string | null;
}

export interface ApiKeysResponse {
  ok: boolean;
  environment: PortalEnvironment;
  public_key: string;
  secret_key_masked: string;
  secret_key?: string;
  error?: string;
}

export interface DisputeRow {
  id: string;
  transaction_id: string;
  amount_display: string;
  currency: string;
  status: string;
  deadline_at: string;
  created_at: string;
  signature: string | null;
}

export interface TeamMember {
  id: string;
  email: string;
  role: MerchantUserRole;
  full_name: string | null;
  totp_enabled: boolean;
  created_at: string;
}

export interface WebhookLog {
  id: string;
  url?: string;
  status_code: number;
  success: boolean;
  response_ms: number;
  created_at: string;
}

export interface MerchantLocation {
  id: string;
  name: string;
  default_fiat_currency: string;
  created_at?: string;
}

export interface PosTerminal {
  id: string;
  label: string;
  status: string;
  location_name: string;
  default_fiat_currency: string;
  activated_at: string | null;
  last_seen_at: string | null;
}

export interface ActivationTokenResponse {
  ok: boolean;
  token: string;
  expires_at: string;
  qr_payload: string;
}
